import { CommonModule } from '@angular/common'
import { Component, Input, OnInit } from '@angular/core'
import { DynamicTextComponent } from '../dynamic-text/dynamic-text.component'
import { ChannelService } from '../../services/channel.service'
import { LocalChannel } from '../../interfaces/local-channel'
import { ChannelTrackData, ChannelTrackDataFlags } from '../../interfaces/channel-track-data'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { EventService } from '../../services/event.service'
import { HTVButtonControllerDirective } from '../../app-button-controller.directive'
import { PAGE_KEY } from '../../constants/page.constants'

interface SubtitleItem {
  label: string
  index: number
  data?: ChannelTrackData
}

@Component({
  selector: 'app-marriott-subtitle',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './marriott-subtitle.component.html',
  styleUrls: ['./marriott-subtitle.component.scss'],
})
export class MarriottSubtitleComponent extends DynamicTextComponent implements OnInit {
  isSubtitleEnabled = false
  subtitleTracks: ChannelTrackData[] = []
  currentSubtitleTrack?: ChannelTrackData

  currentIndex?: number
  currentItem?: SubtitleItem
  subtitleList: SubtitleItem[] = []

  @Input() override singleLine = true
  @Input() override allowOverflow = true

  override enableVisibilityControl = true

  readonly ChannelTrackDataFlags = ChannelTrackDataFlags

  constructor(
    private appButtonDirective: HTVButtonControllerDirective,
    private channelService: ChannelService,
    private eventService: EventService,
    private translateService: TranslateService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()

    this.pageService.updateSubject(PAGE_KEY.TRACK_MENU_OVERRIDE, true)
    this.registerEventListener()
    this.registerChannelListener()
  }

  reset(): void {
    this.isSubtitleEnabled = false
    this.subtitleTracks = []
    this.currentSubtitleTrack = undefined
    this.subtitleList = []
    this.currentIndex = undefined
    this.currentItem = undefined
  }

  registerEventListener(): void {
    this.subscriptions.add(
      this.eventService.keyDown.subscribe((key: string) => {
        switch (key) {
          case 'SUBTITLE':
            this.appButtonDirective.clearSelectedButtons()
            this.nextTrack()
            break
          default:
            break
        }
      })
    )
  }

  registerChannelListener(): void {
    this.subscriptions.add(
      this.channelService.currentChannel$.subscribe((channel: LocalChannel | undefined) => {
        this.reset()

        if (channel) {
          this.subscriptions.add(
            channel.channelData$.subscribe(channelData => {
              if (channelData) {
                this.isSubtitleEnabled = channelData.isSubtitleEnabled

                if (JSON.stringify(this.subtitleTracks) != JSON.stringify(channelData.subtitleTracks)) {
                  this.subtitleTracks = channelData.subtitleTracks
                  this.subtitleList = channelData.subtitleTracks.map((e, index) => this.mapToSubtitleItem(e, index))
                }

                if (
                  JSON.stringify(this.currentSubtitleTrack?.data) != JSON.stringify(channelData.currentSubtitleTrack)
                ) {
                  if (channelData.currentSubtitleTrack) {
                    this.currentSubtitleTrack = channelData.currentSubtitleTrack
                  } else {
                    this.currentSubtitleTrack = undefined
                  }

                  const index = this.subtitleTracks.findIndex(e => e.index === this.currentSubtitleTrack?.index)
                  if (index > -1) {
                    this.currentIndex = index
                    this.currentItem = this.subtitleList[this.currentIndex]
                  }
                }
              } else {
                this.reset()
              }
            })
          )
        }
      })
    )
  }

  mapToSubtitleItem(data: ChannelTrackData, index: number): SubtitleItem {
    let label: string
    switch (data.lang) {
      case 'none':
      case 'yyy':
        label = this.translateService.instant('CHANNEL_SUBTITLE.NONE')
        break
      case 'qaa':
        label = this.translateService.instant('CHANNEL_SUBTITLE.ORIGINAL_VERSION.ABR')
        break
      case 'qad':
        label = this.translateService.instant('CHANNEL_SUBTITLE.AUDIO_DESCRIPTION.ABR')
        break
      default:
        label = data.lang
        break
    }
    if (data.flags.indexOf(ChannelTrackDataFlags.HEARING_IMPAIRED) > -1) {
      label = `${label}(${this.translateService.instant('CHANNEL_SUBTITLE.CLOSED_CAPTION.ABR')})`
    }
    if (data.flags.indexOf(ChannelTrackDataFlags.VISUAL_IMPAIRED) > -1) {
      label = `${label}(${this.translateService.instant('CHANNEL_SUBTITLE.AUDIO_DESCRIPTION.ABR')})`
    }
    return {
      label: label,
      index: index,
      data: data,
    }
  }

  selectTrack(item: SubtitleItem): void {
    if (item.index === this.currentIndex) {
      this.channelService.setSubtitleState(!this.isSubtitleEnabled)
    } else {
      if (item.data) {
        this.channelService.setSubtitleTrack(item.data, true)
      }
    }
  }

  nextTrack(): void {
    if (this.isSubtitleEnabled) {
      let nextIndex
      let nextItem
      let enableSubtitle = true

      if (this.currentIndex != undefined) {
        if (this.currentIndex < this.subtitleList.length - 1) {
          nextIndex = this.currentIndex + 1
          nextItem = this.subtitleList[nextIndex]
        } else {
          nextIndex = 0
          nextItem = this.subtitleList[0]
          enableSubtitle = false
        }
      } else {
        if (this.subtitleList.length > 0) {
          nextIndex = 0
          nextItem = this.subtitleList[0]
        }
      }

      if (nextItem) {
        if (nextItem.data) {
          this.channelService.setSubtitleTrack(nextItem.data, enableSubtitle)
        }
      }
    } else {
      let nextItem
      if (this.subtitleList.length > 0) {
        nextItem = this.subtitleList[0]
      }

      if (nextItem?.data) {
        this.channelService.setSubtitleTrack(nextItem.data, true)
      }
    }
  }
}
